import type { ContentStatus } from '@cookspace-shared-contracts/core';

import { baseApi } from '@/api/baseApi';
import type {
    ApproveContentResponse,
    GetModerationArticleResponse,
    GetModerationCourseResponse,
    GetModerationEventResponse,
    GetModerationQueueResponse,
    GetModerationRecipeResponse,
    GetModerationStreamResponse,
    ModerationContentType,
    RejectContentRequest,
    RejectContentResponse
} from '@/lib/moderationSchemas';
import {
    ApproveContentResponseSchema,
    GetModerationArticleResponseSchema,
    GetModerationCourseResponseSchema,
    GetModerationEventResponseSchema,
    GetModerationQueueResponseSchema,
    GetModerationRecipeResponseSchema,
    GetModerationStreamResponseSchema,
    RejectContentResponseSchema
} from '@/lib/moderationSchemas';
import { parseWithSchema } from '@/lib/safeParse';

const CONTENT_TAG_BY_TYPE = {
    recipe: 'Recipe',
    article: 'Article',
    course: 'Course',
    stream: 'Stream',
    event: 'Event'
} as const satisfies Record<
    ModerationContentType,
    'Recipe' | 'Article' | 'Course' | 'Stream' | 'Event'
>;

/**
 * RTK Query endpoints для admin-backend /moderation/*.
 * admin-backend на порту 5002 без префикса /admin.
 */

interface GetModerationQueueArg {
    type?: ModerationContentType;
    status?: ContentStatus;
    page?: number;
}

export const moderationApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getModerationQueue: build.query<GetModerationQueueResponse, GetModerationQueueArg | void>({
            query: (params) => {
                const search: Record<string, string> = {};
                if (params?.type) {
                    search.type = params.type;
                }
                if (params?.status) {
                    search.status = params.status;
                }
                if (params?.page !== undefined) {
                    search.page = String(params.page);
                }
                return {
                    url: '/moderation/queue',
                    method: 'GET',
                    params: search
                };
            },
            transformResponse: (raw: unknown): GetModerationQueueResponse =>
                parseWithSchema(GetModerationQueueResponseSchema, raw),
            providesTags: (result) =>
                result
                    ? [
                        ...result.items.map((item) => ({
                            type: 'Moderation' as const,
                            id: `${item.type}-${item.id}`
                        })),
                        { type: 'Moderation' as const, id: 'LIST' }
                    ]
                    : [{ type: 'Moderation' as const, id: 'LIST' }]
        }),

        getModerationRecipe: build.query<GetModerationRecipeResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/moderation/recipe/${id}`,
                method: 'GET'
            }),
            transformResponse: (raw: unknown): GetModerationRecipeResponse =>
                parseWithSchema(GetModerationRecipeResponseSchema, raw),
            providesTags: (_result, _error, arg) => [
                { type: 'Moderation' as const, id: `recipe-${arg.id}` }
            ]
        }),

        getModerationArticle: build.query<GetModerationArticleResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/moderation/article/${id}`,
                method: 'GET'
            }),
            transformResponse: (raw: unknown): GetModerationArticleResponse =>
                parseWithSchema(GetModerationArticleResponseSchema, raw),
            providesTags: (_result, _error, arg) => [
                { type: 'Moderation' as const, id: `article-${arg.id}` }
            ]
        }),

        getModerationCourse: build.query<GetModerationCourseResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/moderation/course/${id}`,
                method: 'GET'
            }),
            transformResponse: (raw: unknown): GetModerationCourseResponse =>
                parseWithSchema(GetModerationCourseResponseSchema, raw),
            providesTags: (_result, _error, arg) => [
                { type: 'Moderation' as const, id: `course-${arg.id}` }
            ]
        }),

        getModerationStream: build.query<GetModerationStreamResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/moderation/stream/${id}`,
                method: 'GET'
            }),
            transformResponse: (raw: unknown): GetModerationStreamResponse =>
                parseWithSchema(GetModerationStreamResponseSchema, raw),
            providesTags: (_result, _error, arg) => [
                { type: 'Moderation' as const, id: `stream-${arg.id}` }
            ]
        }),

        getModerationEvent: build.query<GetModerationEventResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/moderation/event/${id}`,
                method: 'GET'
            }),
            transformResponse: (raw: unknown): GetModerationEventResponse =>
                parseWithSchema(GetModerationEventResponseSchema, raw),
            providesTags: (_result, _error, arg) => [
                { type: 'Moderation' as const, id: `event-${arg.id}` }
            ]
        }),

        approveContent: build.mutation<
            ApproveContentResponse,
            { type: ModerationContentType; id: string }
        >({
            query: ({ type, id }) => ({
                url: `/moderation/${type}/${id}/approve`,
                method: 'POST',
                body: {}
            }),
            transformResponse: (raw: unknown): ApproveContentResponse =>
                parseWithSchema(ApproveContentResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'Moderation', id: `${arg.type}-${arg.id}` },
                { type: 'Moderation', id: 'LIST' },
                { type: CONTENT_TAG_BY_TYPE[arg.type], id: arg.id },
                { type: CONTENT_TAG_BY_TYPE[arg.type], id: 'LIST' }
            ]
        }),

        rejectContent: build.mutation<
            RejectContentResponse,
            { type: ModerationContentType; id: string; body: RejectContentRequest }
        >({
            query: ({ type, id, body }) => ({
                url: `/moderation/${type}/${id}/reject`,
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): RejectContentResponse =>
                parseWithSchema(RejectContentResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'Moderation', id: `${arg.type}-${arg.id}` },
                { type: 'Moderation', id: 'LIST' },
                { type: CONTENT_TAG_BY_TYPE[arg.type], id: arg.id },
                { type: CONTENT_TAG_BY_TYPE[arg.type], id: 'LIST' }
            ]
        })
    })
});

export const {
    useGetModerationQueueQuery,
    useGetModerationRecipeQuery,
    useGetModerationArticleQuery,
    useGetModerationCourseQuery,
    useGetModerationStreamQuery,
    useGetModerationEventQuery,
    useApproveContentMutation,
    useRejectContentMutation
} = moderationApi;
